import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import { fillUnitPrice } from "../utils/productPrice";
import UnitPriceInput from "./UnitPriceInput";
import ImportExportBar from "./ImportExportBar";
import CustomerSelect from "./CustomerSelect";

const emptyItem = { productId: "", quantity: "", unitPrice: "" };
const emptyForm = { customerId: "", orderDate: "", source: "APP_SINGLE_ENTRY", remarks: "", items: [{ ...emptyItem }] };

const statusBadgeClass = {
  DRAFT: "badge-gray",
  CONFIRMED: "badge-blue",
  INVOICED: "badge-green",
  CANCELLED: "badge-red",
};

const SalesOrders = () => {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const customerById = useMemo(() => {
    const map = new Map();
    customers.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [customers]);

  const loadOptions = async () => {
    try {
      const [customersData, productsData] = await Promise.all([
        api.getAll("/api/v1/customers"),
        api.get("/api/v1/products", { size: 200 }),
      ]);
      setCustomers(customersData?.content || []);
      setProducts(productsData?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load customers / products");
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/sales-orders", { size: 200, status: statusFilter || undefined });
      setOrders(data?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load sales orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOptions();
  }, []);

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const openCreate = () => {
    setFormData(emptyForm);
    setShowModal(true);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      // a newly picked product brings its own prices
      if (field === "productId") {
        items[index].unitPrice = "";
        items[index].priceOptions = [];
      }
      return { ...prev, items };
    });
    if (field === "productId") fillUnitPrice(setFormData, index, value, "sales");
  };

  const addItemRow = () => {
    setFormData((prev) => ({ ...prev, items: [...prev.items, { ...emptyItem }] }));
  };

  const removeItemRow = (index) => {
    setFormData((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerId) {
      toast.error("Please select a customer");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customerId: Number(formData.customerId),
        orderDate: formData.orderDate,
        source: formData.source,
        remarks: formData.remarks || null,
        items: formData.items.map((item) => ({
          productId: Number(item.productId),
          quantity: Number(item.quantity),
          unitPrice: item.unitPrice === "" ? null : Number(item.unitPrice),
        })),
      };
      await api.post("/api/v1/sales-orders", payload);
      toast.success("Sales order created");
      setShowModal(false);
      loadOrders();
    } catch (error) {
      toast.error(error.message || "Failed to create sales order");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async (id) => {
    setActioningId(id);
    try {
      await api.put(`/api/v1/sales-orders/${id}/confirm`);
      toast.success("Sales order confirmed — invoice created");
      loadOrders();
    } catch (error) {
      toast.error(error.message || "Failed to confirm sales order");
    } finally {
      setActioningId(null);
    }
  };

  const handleCancel = async (id) => {
    setActioningId(id);
    try {
      await api.put(`/api/v1/sales-orders/${id}/cancel`, "Cancelled from admin panel");
      toast.success("Sales order cancelled");
      loadOrders();
    } catch (error) {
      toast.error(error.message || "Failed to cancel sales order");
    } finally {
      setActioningId(null);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading sales orders...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="CONFIRMED">Confirmed</option>
          <option value="INVOICED">Invoiced</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        <button className="primary" onClick={openCreate}>
          + New Sales Order
        </button>
        <ImportExportBar
          importPath="/api/v1/sales-orders/import"
          exportPath="/api/v1/sales-orders/export"
          exportFilename="sales-orders.xlsx"
          extraParams={{ status: statusFilter || undefined }}
          label="Orders"
          onImported={loadOrders}
        />
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Order #</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Total</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={7}>No sales orders found.</td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.id}>
                  <td>{order.id}</td>
                  <td>{order.orderNumber}</td>
                  <td>{customerById.get(order.customerId) || order.customerId}</td>
                  <td>{order.orderDate}</td>
                  <td>{order.totalAmount}</td>
                  <td>
                    <span className={`badge ${statusBadgeClass[order.status] || "badge-gray"}`}>{order.status}</span>
                  </td>
                  <td className="action-buttons flex gap-2">
                    {order.status === "DRAFT" ? (
                      <>
                        <button className="primary" disabled={actioningId === order.id} onClick={() => handleConfirm(order.id)}>
                          {actioningId === order.id ? "..." : "Confirm"}
                        </button>
                        <button className="close-btn" disabled={actioningId === order.id} onClick={() => handleCancel(order.id)}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box" style={{ maxWidth: "820px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Create Sales Order</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label>Customer (filter by salesman / beats, search by code or name)</label>
                  <CustomerSelect
                    customers={customers}
                    value={formData.customerId}
                    onChange={(value) => handleFieldChange({ target: { name: "customerId", value } })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Order Date</label>
                  <input type="date" onClick={(e) => e.target.showPicker?.()} name="orderDate" value={formData.orderDate} onChange={handleFieldChange} required />
                </div>
                <div className="form-group">
                  <label>Source</label>
                  <select name="source" value={formData.source} onChange={handleFieldChange}>
                    <option value="APP_SINGLE_ENTRY">App Single Entry</option>
                    <option value="EXCEL_IMPORT">Excel Import</option>
                    <option value="BRAND_PORTAL_IMPORT">Brand Portal Import</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Remarks</label>
                  <input name="remarks" value={formData.remarks} onChange={handleFieldChange} />
                </div>
              </div>

              <h4 style={{ marginTop: "20px", marginBottom: "8px", fontWeight: 600 }}>
                Items <span className="page-subtitle">(unit price is filled from the product's sales price in its price lists)</span>
              </h4>
              <div className="table-wrapper">
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Unit Price</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <select
                            value={item.productId}
                            onChange={(e) => handleItemChange(index, "productId", e.target.value)}
                            required
                          >
                            <option value="">Select product</option>
                            {products.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name} ({p.skuCode})
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            style={{ width: "100px" }}
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                            required
                          />
                        </td>
                        <td>
                          <UnitPriceInput
                            value={item.unitPrice}
                            onChange={(value) => handleItemChange(index, "unitPrice", value)}
                            options={item.priceOptions}
                            placeholder="auto"
                          />
                        </td>
                        <td>
                          {formData.items.length > 1 ? (
                            <button type="button" className="close-btn" onClick={() => removeItemRow(index)}>
                              &times;
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" className="secondary" style={{ marginTop: "10px" }} onClick={addItemRow}>
                + Add Item
              </button>

              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SalesOrders;
